import { useEffect, useState } from "react";
import { Dropdown } from "antd";
import type { MenuProps } from "antd";
import { DownOutlined, UserOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { SchemaWaiterOption } from "./useSchemaWaiterFilter";

/**
 * Полето „Сервитьор" горе вдясно в картата със схемата — линк с падащо меню:
 * чии маси се показват (влезлият пръв, после останалите по азбучен ред).
 * Отварянето опреснява сметките. Esc затваря САМО менюто (поглъща се — не
 * стига нито до избрания ред от поръчката, нито до глобалното „назад").
 * Извън платното: кликът не мести и не маха избора.
 */
export const SchemaWaiterFilter = ({
  options,
  selectedWaiterId,
  onChange,
  onOpen,
}: {
  options: SchemaWaiterOption[];
  selectedWaiterId: number;
  onChange: (waiterId: number) => void;
  onOpen: () => void;
}) => {
  const { t } = useTranslation();
  const nameOf = (option: SchemaWaiterOption | undefined) =>
    option?.name ?? t("ordering.schema.unknownWaiter");
  const selectedName = nameOf(options.find((option) => option.waiterId === selectedWaiterId));
  const label = t("ordering.schema.waiterFilter");
  const [open, setOpen] = useState(false);

  // Esc при отворено меню — само го затваря (capture фаза, преди другите).
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopPropagation();
      setOpen(false);
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [open]);

  const items: MenuProps["items"] = options.map((option) => ({
    key: String(option.waiterId),
    label: nameOf(option),
  }));

  return (
    <Dropdown
      trigger={["click"]}
      placement="bottomRight"
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) onOpen();
      }}
      menu={{
        items,
        selectedKeys: [String(selectedWaiterId)],
        onClick: ({ key }) => {
          setOpen(false);
          onChange(Number(key));
        },
      }}
    >
      <button
        type="button"
        title={label}
        aria-label={`${label}: ${selectedName}`}
        className="absolute right-3 top-2 z-30 inline-flex h-8 max-w-[260px] cursor-pointer items-center gap-1.5 rounded-[9px] border-0 bg-surface/85 px-2.5 text-[13px] font-semibold text-pos-accent-ink underline-offset-2 outline-none hover:underline focus-visible:shadow-[0_0_0_3px_var(--color-brand-tint)]"
      >
        <UserOutlined className="shrink-0 text-[13px]" />
        <span className="min-w-0 truncate">{selectedName}</span>
        <DownOutlined className="shrink-0 text-[10px]" />
      </button>
    </Dropdown>
  );
};
